import { useSelect } from 'downshift';
import ChevronDown from '~/icons/iconography/chevron/down.svg';
import { DropdownItem } from './models';
import stateReducer from './stateReducer/StateReducer';
import {
    Item,
    ItemCheckbox,
    ItemCount,
    ItemLabel,
    ItemLeftContainer,
    List,
    StyledButton,
} from './styled';

type DropdownSelector = {
    options: DropdownItem[];
    label: string;
    noLabel?: boolean;
    id: string;
    setSelectedItems: (items: DropdownItem[]) => void;
    selectedItems: DropdownItem[];
};

export default function DropdownSelector({
    options,
    label,
    noLabel = false,
    setSelectedItems,
    selectedItems,
}: DropdownSelector) {
    const {
        isOpen,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
    } = useSelect({
        items: options,
        stateReducer,
        selectedItem: null,
        onSelectedItemChange: ({ selectedItem }) => {
            if (!selectedItem) {
                return;
            }

            const index = selectedItems.indexOf(selectedItem);

            if (index > 0) {
                setSelectedItems([
                    ...selectedItems.slice(0, index),
                    ...selectedItems.slice(index + 1),
                ]);
            } else if (index === 0) {
                setSelectedItems([...selectedItems.slice(1)]);
            } else {
                setSelectedItems([...selectedItems, selectedItem]);
            }
        },
    });

    const isListHidden = !isOpen && !noLabel;

    return (
        <>
            <StyledButton
                {...getToggleButtonProps()}
                isOpen={isOpen}
                noLabel={noLabel}
                tabIndex={0}
            >
                <span {...getLabelProps()}>{label}</span>
                <ChevronDown />
            </StyledButton>

            <List
                aria-hidden={isListHidden}
                {...getMenuProps()}
                isHidden={isListHidden}
                noLabel={noLabel}
            >
                {options.map((item, index) => (
                    <Item
                        highlighted={highlightedIndex === index}
                        key={item.label}
                        {...getItemProps({
                            item,
                            index,
                        })}
                    >
                        <ItemLeftContainer>
                            <ItemCheckbox
                                type="checkbox"
                                checked={selectedItems.some(
                                    (selected) => selected.value === item.value,
                                )}
                                readOnly
                                value={item.value}
                            />
                            <ItemLabel>{item.label}</ItemLabel>
                        </ItemLeftContainer>

                        {item.count ? <ItemCount>({item.count})</ItemCount> : null}
                    </Item>
                ))}
            </List>
        </>
    );
}
